import jwtAuthKioskAxios from "../auth/jwtAuthKiosk";
import { setBackendDomain, getBEUrl } from "./management";
import { axios as publicAxios } from "../config";
import { getBackendBaseUrlFromEnv } from "app/helpers/dsok/dailyMenuUrl";

// POST /daily-menu/mailchimp/test — overí spojenie a audience (nič neodosiela).
export const mailchimpTestConnection = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/api/v1/daily-menu/mailchimp/test`,
    {},
  );
  return data;
};

// POST /daily-menu/mailchimp/test-email — pošle TESTOVACÍ e-mail na zadanú adresu.
// payload: { email, days, week, title, brandName, copy }
export const mailchimpTestEmail = async (payload) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/api/v1/daily-menu/mailchimp/test-email`,
    payload,
  );
  return data;
};

// POST /daily-menu/mailchimp/send — reálne odošle týždenné menu ako kampaň.
export const mailchimpSend = async (payload) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/api/v1/daily-menu/mailchimp/send`,
    payload,
  );
  return data;
};

// POST /daily-menu/docx — server vygeneruje jedálny lístok do .docx (logo načíta sám).
// payload: { days, title, brandName, logoUrl, copy }. Vráti Blob (.docx).
export const generateDailyMenuDocx = async (payload) => {
  setBackendDomain(jwtAuthKioskAxios);
  const res = await jwtAuthKioskAxios.post(`/api/v1/daily-menu/docx`, payload, {
    responseType: "blob",
  });
  return res.data;
};

// GET /daily-menu/id/:id
// Backend môže vrátiť { code, status, dailyMenu: record } alebo priamo record.
export const getDailyMenuById = async (id) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(`/api/v1/daily-menu/id/${id}`);
  const body = data?.data ?? data;
  return body;
};

// GET /daily-menu
// Backend vracia { code: 200, status: "OK", dailyMenu: [{ _id, customerCode, menus: [...], updatedAt }] }
// customerCode sa posiela namiesto customerId.
export const getDailyMenuList = async (customerCode) => {
  setBackendDomain(jwtAuthKioskAxios);
  const query =
    typeof customerCode === "string" && customerCode.trim()
      ? `?customerCode=${encodeURIComponent(customerCode.trim())}`
      : "";
  const { data } = await jwtAuthKioskAxios.get(`/api/v1/daily-menu${query}`);
  const body = data?.data ?? data;
  return body;
};

// PUT /daily-menu/id/:id – v payload sa posiela customerCode, nie customerId.
export const updateDailyMenuById = async (id, payload) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { customerId, ...rest } = payload || {};
  const body = {
    ...rest,
    customerCode: payload?.customerCode ?? rest?.customerCode,
  };
  const { data } = await jwtAuthKioskAxios.put(
    `/api/v1/daily-menu/id/${id}`,
    body,
  );
  return data?.data || data;
};

// POST /daily-menu – v payload sa posiela customerCode, nie customerId.
export const createDailyMenu = async (payload) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { customerId, ...rest } = payload || {};
  const body = {
    ...rest,
    customerCode: payload?.customerCode ?? rest?.customerCode,
  };
  const { data } = await jwtAuthKioskAxios.post(`/api/v1/daily-menu`, body);
  return data?.data || data;
};

// DELETE /daily-menu/id/:id
export const deleteDailyMenuById = async (id) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.delete(
    `/api/v1/daily-menu/id/${id}`,
  );
  return data?.data || data;
};

// GET /daily-menu/settings
export const getDailyMenuSettings = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(`/api/v1/daily-menu/settings`);
  const body = data?.data ?? data;
  return body;
};

// PUT /daily-menu/settings
export const updateDailyMenuSettings = async (settings) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.put(
    `/api/v1/daily-menu/settings`,
    settings,
  );
  return data?.data ?? data;
};

// POST /daily-menu/refresh – pošle WebSocket signál obrazovkám zákazníka, aby sa obnovili.
export const refreshDailyMenuScreen = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(`/api/v1/daily-menu/refresh`);
  return data?.data ?? data;
};

// POST /daily-menu/publish-web-export – vygeneruje a zverejní WEB export JSON pre
// CELÉHO zákazníka naraz (breakfast/lunch/dinner × aktuálny/nasledujúci týždeň).
// Vráti { published: [{ fileName, publicPath, menuType, weekStartDate, url }], skipped: [...] }.
export const publishDailyMenuWebExport = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/api/v1/daily-menu/publish-web-export`,
  );
  const body = data?.data ?? data;
  const baseUrl = getBEUrl();
  const withUrl = (item) => ({
    ...item,
    url: baseUrl && item?.publicPath ? `${baseUrl}${item.publicPath}` : item?.publicPath,
  });
  return {
    published: (body?.published || []).map(withUrl),
    skipped: body?.skipped || [],
  };
};

// GET by-code (public, no auth) – for daily-menu-screen
// Backend: GET /api/V1/daily-menu/by-code/:customerCode/:menuId
export const getDailyMenuScreenData = async ({
  customerCode,
  locationCode,
  env,
  menuId,
}) => {
  const params = new URLSearchParams(window.location.search);
  const environment = params.get("env") ?? env ?? "";
  const base = getBackendBaseUrlFromEnv(environment);
  const path = `api/V1/daily-menu/by-code/${encodeURIComponent(customerCode || "")}`;
  const url = base ? `${base}/${path}` : `/${path}`;
  try {
    // _t cache-busting – na Androide/WebView bez neho môže GET vrátiť starú (cache) verziu.
    const { data } = await publicAxios.get(url, { params: { _t: Date.now() } });
    const body = data?.data ?? data;
    return body;
  } catch (e) {
    const response = e?.response;
    const body = response?.data;
    const message =
      (body && typeof body === "object" && body.message) ||
      (typeof body === "string" ? body : null) ||
      e?.message ||
      "Nepodarilo sa načítať denné menu.";
    const err = new Error(message);
    err.code = body?.code ?? response?.status;
    err.status = body?.status;
    err.response = response;
    throw err;
  }
};

// GET by-code/:customerCode/settings-only (public, no auth) – ľahká verzia by-code,
// vráti iba settings (bez menus/days). Používa sa pred vykreslením obrazovky na
// rýchle zistenie nastavenia otočenia obsahu (contentRotation).
export const getDailyMenuScreenSettingsByCode = async ({
  customerCode,
  env,
}) => {
  const params = new URLSearchParams(window.location.search);
  const environment = params.get("env") ?? env ?? "";
  const base = getBackendBaseUrlFromEnv(environment);
  const path = `api/V1/daily-menu/by-code/${encodeURIComponent(customerCode || "")}/settings-only`;
  const url = base ? `${base}/${path}` : `/${path}`;
  try {
    // _t cache-busting – aby settings (vrátane template) neprišli z cache po refreshi.
    const { data } = await publicAxios.get(url, { params: { _t: Date.now() } });
    const body = data?.data ?? data;
    return body;
  } catch (e) {
    return { settings: {} };
  }
};
